"use client";

import {
  colors,
  fonts,
  fontSizes,
  radii,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import type {
  ComponentPropsWithoutRef,
  KeyboardEventHandler,
  ReactNode,
  Ref,
} from "react";

import { SearchGlyph } from "./search-glyph";
import { SearchFieldInput } from "./search-field-input";
import { SearchShortcutHint } from "./search-shortcut-hint";

export type SearchFieldLinkProps = ComponentPropsWithoutRef<"a"> &
  Readonly<{
    href: string;
  }>;

export type SearchFieldProps = Readonly<{
  variant: "rail" | "masthead" | "drawer";
}> &
  (
    | Readonly<{
        active: false;
        href: string;
        shortcutHint: string | null;
        onActivate: () => void;
        renderLink: (props: SearchFieldLinkProps) => ReactNode;
      }>
    | Readonly<{
        active: true;
        query: string;
        inputRef: Ref<HTMLInputElement>;
        onQueryChange: (value: string) => void;
        onClear: () => void;
        onKeyDown: KeyboardEventHandler<HTMLInputElement>;
      }>
  );

const styles = stylex.create({
  row: {
    display: "flex",
    alignItems: "center",
    gap: spacing.space12,
    boxSizing: "border-box",
    minWidth: 0,
    color: colors.foreground,
    fontWeight: 400,
    textDecoration: "none",
  },
  field: {
    height: 36,
    paddingInline: spacing.space12,
    borderRadius: radii.small,
    fontFamily: fonts.sans,
    fontSize: fontSizes.label,
    lineHeight: 1.5,
    letterSpacing: "0.3px",
    boxShadow: {
      default: `inset 0 0 0 1px ${colors.borderStrong}`,
      ":focus-within": `inset 0 0 0 1px ${colors.foreground}`,
    },
  },
  rail: { marginBottom: spacing.space8 },
  masthead: { width: "100%" },
  drawer: {
    width: "100%",
    paddingBlock: spacing.space16,
    paddingInlineStart: spacing.space12,
    paddingInlineEnd: spacing.space24,
    fontFamily: fonts.mono,
    fontSize: fontSizes.title,
    lineHeight: 1.3,
    letterSpacing: "0.2px",
  },
  resting: {
    color: colors.secondary,
    backgroundColor: { default: "transparent", ":hover": colors.hover },
    outline: {
      default: "none",
      ":focus-visible": `2px solid ${colors.foreground}`,
    },
    outlineOffset: 3,
  },
});

export function SearchField(props: SearchFieldProps) {
  const field = props.variant !== "drawer";
  const rowStyle = stylex.props(
    styles.row,
    field && styles.field,
    styles[props.variant],
    !props.active && styles.resting,
  );

  if (!props.active) {
    return props.renderLink({
      href: props.href,
      onClick: props.onActivate,
      ...rowStyle,
      children: (
        <>
          <SearchGlyph />
          Search
          <SearchShortcutHint shortcutHint={props.shortcutHint} field={field} />
        </>
      ),
    });
  }

  const { inputRef, query, onQueryChange, onKeyDown, onClear } = props;
  return (
    <div {...rowStyle}>
      <SearchGlyph />
      <SearchFieldInput
        inputRef={inputRef}
        query={query}
        onQueryChange={onQueryChange}
        onKeyDown={onKeyDown}
        onClear={onClear}
      />
    </div>
  );
}
